import CTASection from "../components/CTASection";
import { COMPLIANCE_DATA } from "../constants/compliance";

export default function Privacy() {
  return (
    <div id="main-content" className="pt-20" data-testid="privacy-page">
      <section className="py-32 px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto">
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold mb-6 text-gray-100" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
            Privacy Policy
          </h1>
          <p className="text-sm text-gray-400 mb-8">Last Updated: January 2026</p>

          <div className="space-y-8 text-gray-300">
            <div className="p-6 bg-[#0f0f0f] border border-gray-800 rounded-lg">
              <h2 className="text-2xl font-semibold mb-4 text-[#d4af69]">Introduction</h2>
              <p className="text-gray-400">
                {COMPLIANCE_DATA.legalName} ("we", "us", "our") respects your privacy and is committed to protecting your personal data. 
                This privacy policy explains how we collect, use, disclose, and safeguard your information when you visit our website.
              </p>
            </div>

            <div className="p-6 bg-[#0f0f0f] border border-gray-800 rounded-lg">
              <h2 className="text-2xl font-semibold mb-4 text-[#d4af69]">Information We Collect</h2>
              <h3 className="text-lg font-medium mb-3 text-gray-200">Personal Information</h3>
              <p className="text-gray-400 mb-4">We may collect the following personal information:</p>
              <ul className="space-y-2 text-gray-400 text-sm ml-6">
                <li className="list-disc">Name</li>
                <li className="list-disc">Email address</li>
                <li className="list-disc">Phone number</li>
                <li className="list-disc">City/location</li>
                <li className="list-disc">Investment interests</li>
              </ul>

              <h3 className="text-lg font-medium mb-3 mt-6 text-gray-200">Automatically Collected Information</h3>
              <ul className="space-y-2 text-gray-400 text-sm ml-6">
                <li className="list-disc">IP address</li>
                <li className="list-disc">Browser type and version</li>
                <li className="list-disc">Device information</li>
                <li className="list-disc">Pages visited and time spent</li>
                <li className="list-disc">Referring website</li>
              </ul>
            </div>

            <div className="p-6 bg-[#0f0f0f] border border-gray-800 rounded-lg">
              <h2 className="text-2xl font-semibold mb-4 text-[#d4af69]">How We Use Your Information</h2>
              <ul className="space-y-3 text-gray-400 text-sm">
                <li className="flex items-start gap-2">
                  <span className="text-[#d4af69] mt-1">•</span>
                  <span>To respond to your inquiries and provide information about our services</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-[#d4af69] mt-1">•</span>
                  <span>To communicate with you regarding mutual fund and SIF distribution services</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-[#d4af69] mt-1">•</span>
                  <span>To improve our website and services</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-[#d4af69] mt-1">•</span>
                  <span>To comply with legal and regulatory obligations</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-[#d4af69] mt-1">•</span>
                  <span>To analyze website usage and optimize user experience</span>
                </li>
              </ul>
            </div>

            <div className="p-6 bg-[#0f0f0f] border border-gray-800 rounded-lg">
              <h2 className="text-2xl font-semibold mb-4 text-[#d4af69]">Sharing of Information</h2>
              <p className="text-gray-400 mb-4">We may share your information with:</p>
              <ul className="space-y-2 text-gray-400 text-sm ml-6">
                <li className="list-disc"><strong className="text-gray-200">Service Providers:</strong> CRM platforms, email service providers, analytics tools</li>
                <li className="list-disc"><strong className="text-gray-200">Regulatory Authorities:</strong> When required by law or regulation</li>
                <li className="list-disc"><strong className="text-gray-200">Business Partners:</strong> With your consent, for distribution services</li>
              </ul>
              <p className="text-gray-400 mt-4 text-sm">
                We do not sell, rent, or trade your personal information to third parties for marketing purposes.
              </p>
            </div>

            <div className="p-6 bg-[#0f0f0f] border border-gray-800 rounded-lg">
              <h2 className="text-2xl font-semibold mb-4 text-[#d4af69]">Data Retention</h2>
              <p className="text-gray-400">
                We retain your personal information for as long as necessary to fulfill the purposes outlined in this policy, 
                unless a longer retention period is required or permitted by law. Lead information is typically retained for 
                a period of 7 years in accordance with regulatory requirements.
              </p>
            </div>

            <div className="p-6 bg-[#0f0f0f] border border-gray-800 rounded-lg">
              <h2 className="text-2xl font-semibold mb-4 text-[#d4af69]">Your Rights</h2>
              <p className="text-gray-400 mb-4">You have the right to:</p>
              <ul className="space-y-2 text-gray-400 text-sm ml-6">
                <li className="list-disc">Access your personal information</li>
                <li className="list-disc">Correct inaccurate or incomplete data</li>
                <li className="list-disc">Request deletion of your data (subject to legal requirements)</li>
                <li className="list-disc">Withdraw consent for marketing communications</li>
                <li className="list-disc">Object to processing of your data in certain circumstances</li>
              </ul>
              <p className="text-gray-400 mt-4">
                To exercise these rights, please contact us at{" "}
                <a href={`mailto:${COMPLIANCE_DATA.contactEmail}`} className="text-[#d4af69] hover:underline">
                  {COMPLIANCE_DATA.contactEmail}
                </a>
              </p>
            </div>

            <div className="p-6 bg-[#0f0f0f] border border-gray-800 rounded-lg">
              <h2 className="text-2xl font-semibold mb-4 text-[#d4af69]">Security Measures</h2>
              <p className="text-gray-400 mb-4">
                We implement appropriate technical and organizational security measures to protect your personal information:
              </p>
              <ul className="space-y-2 text-gray-400 text-sm ml-6">
                <li className="list-disc">Encryption of data in transit and at rest</li>
                <li className="list-disc">Restricted access controls</li>
                <li className="list-disc">Regular security assessments</li>
                <li className="list-disc">Secure server infrastructure</li>
              </ul>
            </div>

            <div className="p-6 bg-[#0f0f0f] border border-gray-800 rounded-lg">
              <h2 className="text-2xl font-semibold mb-4 text-[#d4af69]">Cookies and Tracking</h2>
              <p className="text-gray-400 mb-4">
                Our website uses cookies and similar tracking technologies. For detailed information, please see our{" "}
                <a href="/cookie-policy" className="text-[#d4af69] hover:underline">Cookie Policy</a>.
              </p>
            </div>

            <div className="p-6 bg-[#0f0f0f] border border-gray-800 rounded-lg">
              <h2 className="text-2xl font-semibold mb-4 text-[#d4af69]">Changes to This Policy</h2>
              <p className="text-gray-400">
                We may update this privacy policy from time to time. Any changes will be posted on this page with an updated 
                revision date. We encourage you to review this policy periodically.
              </p>
            </div>

            <div className="p-6 bg-[#0f0f0f] border border-gray-800 rounded-lg">
              <h2 className="text-2xl font-semibold mb-4 text-[#d4af69]">Contact Us</h2>
              <p className="text-gray-400 mb-4">
                For questions or concerns about this privacy policy or our data practices, please contact:
              </p>
              <div className="space-y-2 text-sm text-gray-400">
                <p><strong className="text-gray-200">Email:</strong> {COMPLIANCE_DATA.contactEmail}</p>
                <p><strong className="text-gray-200">Phone:</strong> {COMPLIANCE_DATA.contactPhone}</p>
                <p><strong className="text-gray-200">Address:</strong> {COMPLIANCE_DATA.address}</p>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}